import { useAuth } from "@clerk/clerk-react";
import { createFileRoute, Navigate, Outlet } from "@tanstack/react-router";
import { AppShell } from "@/components/layout/app-shell";
import { StaffGate } from "@/components/layout/staff-gate";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ApiClientProvider } from "@/lib/api/api-client-provider";
import { loadEnv } from "@/lib/env";

// The authenticated layout (TASK.md nav contract): redirects signed-out -> /sign-in, otherwise
// renders the shell (sidebar + header) around every destination. Auth is decided by Clerk's
// own state, not a router-context guess — while it's loading we show nothing (CRAFT §2: <150ms
// expected work shows nothing, and Clerk's local session check is normally well under that).
//
// ApiClientProvider and TooltipProvider live here, not main.tsx (perf-route-splitting): both are
// only ever consumed below this layout (StaffGate, AppShell's sidebar, and every catalog page —
// none of that exists on the public /sign-in route), and this component is already the unit
// TanStack Router's autoCodeSplitting lazy-loads — so both providers, `client.ts`, and
// packages/types' schemas ride along in that same lazy chunk instead of blocking first paint on
// /sign-in. `loadEnv()` re-resolves the already-validated config (cached, see lib/env.ts) rather
// than threading `apiUrl` down through router context for one value.
export const Route = createFileRoute("/_authenticated")({
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { isLoaded, isSignedIn } = useAuth();

  if (!isLoaded) {
    return null;
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" />;
  }

  const { apiUrl } = loadEnv();

  return (
    <ApiClientProvider baseUrl={apiUrl}>
      <TooltipProvider delay={300}>
        <StaffGate>
          <AppShell>
            <Outlet />
          </AppShell>
        </StaffGate>
      </TooltipProvider>
    </ApiClientProvider>
  );
}
